import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Checkbox,
  IconButton,
  useToast,
  Flex,
  Spacer,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { Search, Copy } from "react-feather";
import axios from "axios";
import { useCurrentProfile } from "../../ContextWrapper";

export const ClientVendorSearch = () => {
  const { profile } = useCurrentProfile();
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [showVendorTable, setShowVendorTable] = useState(true);
  const [useIdSearch, setUseIdSearch] = useState(true);
  const [includeNameSearch, setIncludeNameSearch] = useState(false);
  const toast = useToast();

  const visibleSelectedCount = vendors.filter((vendor) =>
    selectedVendors.some((selected) => selected.id === vendor.id)
  ).length;
  const allVisibleSelected =
    vendors.length > 0 && visibleSelectedCount === vendors.length;
  const partiallyVisibleSelected =
    visibleSelectedCount > 0 && visibleSelectedCount < vendors.length;

  const refreshVendorsList = useCallback(() => {
    if (!search.trim()) {
      setVendors([]);
      setLoading(false);
      return;
    }

    let params = {};
    if (search) {
      params.name__icontains = search;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get("/api/v2/vendors/", {
          params,
          withCredentials: true,
        });
        setVendors(response.data.results || []);
      } catch (err) {
        console.error("Error fetching vendors:", err);
        setError("Failed to load vendors");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [search]);

  useEffect(() => {
    refreshVendorsList();
  }, [refreshVendorsList]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setResults(null); // Clear previous results when search changes
    setShowVendorTable(true); // Show vendor table for new search
  };

  const handleSelectVendor = (vendor) => {
    setSelectedVendors((prev) => {
      const exists = prev.find((item) => item.id === vendor.id);
      if (exists) {
        return prev.filter((item) => item.id !== vendor.id);
      }
      return [...prev, vendor];
    });
  };

  const handleSelectAll = () => {
    if (vendors.length === 0) {
      return;
    }

    const visibleVendorIds = new Set(vendors.map((vendor) => vendor.id));
    const allVisibleSelected = vendors.every((vendor) =>
      selectedVendors.some((selected) => selected.id === vendor.id)
    );

    if (allVisibleSelected) {
      setSelectedVendors((prev) =>
        prev.filter((vendor) => !visibleVendorIds.has(vendor.id))
      );
    } else {
      const existingIds = new Set(selectedVendors.map((vendor) => vendor.id));
      const combinedSelection = [
        ...selectedVendors,
        ...vendors.filter((vendor) => !existingIds.has(vendor.id)),
      ];
      setSelectedVendors(combinedSelection);
    }
  };

  const performSearch = async () => {
    if (selectedVendors.length === 0) {
      setError("Please select at least one vendor");
      return;
    }

    if (!useIdSearch && !includeNameSearch) {
      setError("Select at least one match option (vendor ID or name search).");
      return;
    }

    setSearching(true);
    setError(null);

    try {
      // If only one vendor selected, use its exact name
      if (selectedVendors.length === 1) {
        const selectedVendor = selectedVendors[0];
        if (!selectedVendor) {
          throw new Error("Unable to resolve selected vendor details");
        }
        const formData = new FormData();
        if (useIdSearch) {
          formData.append("vendor_id", selectedVendor.id);
        }
        if (includeNameSearch) {
          formData.append("vendor_name", selectedVendor.name);
        }
        formData.append(
          "include_name_search",
          includeNameSearch ? "true" : "false"
        );

        const response = await axios.post("/api/v2/vendors/search_clients/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });

        setResults(response.data);
      } else {
        // Multiple vendors selected - search for each and combine results
        const allResults = {
          vendor_matches: [],
          clients: [],
          summary: {
            total_clients_found: 0,
            total_vendors_matched: 0,
            total_journal_entries: 0,
          }
        };

        const uniqueClients = new Map(); // To deduplicate clients across vendors

        for (const selectedVendor of selectedVendors) {
          if (!selectedVendor) {
            throw new Error("Unable to resolve selected vendor details");
          }
          const formData = new FormData();
          if (useIdSearch) {
            formData.append("vendor_id", selectedVendor.id);
          }
          if (includeNameSearch) {
            formData.append("vendor_name", selectedVendor.name);
          }
          formData.append(
            "include_name_search",
            includeNameSearch ? "true" : "false"
          );
          
          const response = await axios.post("/api/v2/vendors/search_clients/", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          });

          // Merge vendor matches
          allResults.vendor_matches.push(...response.data.vendor_matches);

          // Merge client results (avoiding duplicates by customer-vendor pair)
          response.data.clients.forEach(client => {
            const key = `${client.customer_id}-${client.vendor_id}`;
            if (!uniqueClients.has(key)) {
              uniqueClients.set(key, client);
            }
          });

          // Add to summary
          allResults.summary.total_vendors_matched += response.data.summary.total_vendors_matched;
          allResults.summary.total_journal_entries += response.data.summary.total_journal_entries;
        }

        // Convert unique clients map to array
        allResults.clients = Array.from(uniqueClients.values());
        allResults.summary.total_clients_found = allResults.clients.length;

        // Remove duplicate vendor matches
        const uniqueVendorMatches = new Map();
        allResults.vendor_matches.forEach(vendor => {
          uniqueVendorMatches.set(vendor.id, vendor);
        });
        allResults.vendor_matches = Array.from(uniqueVendorMatches.values());
        allResults.summary.total_vendors_matched = allResults.vendor_matches.length;

        setResults(allResults);
      }

      // Collapse vendor table after successful search
      setShowVendorTable(false);
    } catch (err) {
      setError(err.response?.data?.details || "An error occurred while searching");
    } finally {
      setSearching(false);
    }
  };

  const handleCopyResults = () => {
    if (!results || results.clients.length === 0) {
      toast({
        title: "No Data",
        description: "No results to copy",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const headers = ["Account Name", "Account ID", "Primary Contact", "Email", "Vendor", "Vendor ID", "Transactions", "Total Amount"];
    const csvData = [
      headers.join("\t"),
      ...results.clients.map(client => [
        client.customer_name,
        client.customer_id,
        client.customer_primary_contact || '',
        client.customer_email || '',
        client.vendor_name,
        client.vendor_id,
        client.journal_entries_count,
        formatCurrency(client.total_amount)
      ].join("\t"))
    ].join("\n");

    navigator.clipboard.writeText(csvData).then(() => {
      toast({
        title: "Copied!",
        description: `${results.clients.length} account records copied to clipboard`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }).catch(() => {
      toast({
        title: "Copy Failed",
        description: "Unable to copy to clipboard",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <Box p={6} bg="white" overflowY="auto" h="100%">
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold" mb={2}>
              Account Vendor Search
            </Text>
            <Text color="gray.600">
              Search for accounts that have a specific vendor in their general ledger
            </Text>
          </Box>
          
          <Box w="400px">
            <InputGroup>
              <Input
                placeholder="Search vendors..."
                value={search}
                onChange={handleSearch}
              />
              <InputRightElement>
                <Search size={16} />
              </InputRightElement>
            </InputGroup>
          </Box>
        </Flex>

        {error && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {search.trim() && (
          <Box>
            <Flex justify="space-between" align="center" mb={4}>
              <HStack>
                <Text fontSize="lg" fontWeight="semibold">
                  Select Vendors
                </Text>
                {!showVendorTable && selectedVendors.length > 0 && (
                  <Badge colorScheme="blue" fontSize="xs">
                    {selectedVendors.length} selected
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowVendorTable(!showVendorTable)}
                  ml={2}
                >
                  {showVendorTable ? "Hide" : "Show"} Vendors
                </Button>
              </HStack>
              <Button
                colorScheme="blue"
                onClick={performSearch}
                isLoading={searching}
                loadingText={
                  selectedVendors.length === 1
                    ? "Searching..."
                    : `Searching ${selectedVendors.length} vendors...`
                }
                disabled={
                  selectedVendors.length === 0 ||
                  (!useIdSearch && !includeNameSearch)
                }
              >
                Search for Accounts{" "}
                {selectedVendors.length > 1
                  ? `(${selectedVendors.length} vendors)`
                  : ""}
              </Button>
            </Flex>

            <HStack spacing={6} mb={2}>
              <Checkbox
                isChecked={useIdSearch}
                onChange={(event) => setUseIdSearch(event.target.checked)}
              >
                Match vendor ID
              </Checkbox>
              <Checkbox
                isChecked={includeNameSearch}
                onChange={(event) => setIncludeNameSearch(event.target.checked)}
              >
                Include name search
              </Checkbox>
            </HStack>
            <Text fontSize="xs" color="gray.500" mb={showVendorTable ? 4 : 2}>
              IDs provide the most accurate results. Enable name search to also
              include vendors with similar names.
            </Text>

            {showVendorTable && (
              <>
                <Box maxH="400px" overflowY="auto" borderWidth={1} borderRadius="md">
                  <Table variant="simple" size="sm">
                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                      <Tr>
                        <Th width="50px">
                          <Checkbox
                            isChecked={allVisibleSelected}
                            isIndeterminate={partiallyVisibleSelected}
                            onChange={handleSelectAll}
                          />
                        </Th>
                        <Th>Vendor Name</Th>
                        <Th>ID</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {loading ? (
                        <Tr>
                          <Td colSpan={3} textAlign="center">
                            <Spinner size="md" />
                          </Td>
                        </Tr>
                      ) : vendors.length === 0 ? (
                        <Tr>
                          <Td colSpan={3} textAlign="center">
                            No vendors found.
                          </Td>
                        </Tr>
                      ) : (
                        vendors.map((vendor) => (
                          <Tr key={vendor.id}>
                            <Td>
                              <Checkbox
                                isChecked={selectedVendors.some(
                                  (selected) => selected.id === vendor.id
                                )}
                                onChange={() => handleSelectVendor(vendor)}
                              />
                            </Td>
                            <Td>{vendor.name}</Td>
                            <Td>
                              <Text fontSize="sm" color="gray.500">
                                {vendor.id}
                              </Text>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
                
                <Text fontSize="sm" mt={2}>
                  {selectedVendors.length} vendors selected
                </Text>
              </>
            )}
          </Box>
        )}

        {results && results.clients.length > 0 && (
          <Box>
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontSize="lg" fontWeight="semibold">
                Account Results {selectedVendors.length > 1 ? `(searched ${selectedVendors.length} vendors)` : ""}
              </Text>
              <HStack spacing={6}>
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">{results.summary.total_clients_found}</Text> accounts found
                </Text>
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">{results.summary.total_vendors_matched}</Text> vendors matched
                </Text>
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">{results.summary.total_journal_entries}</Text> transactions
                </Text>
                <IconButton
                  icon={<Copy size={16} />}
                  aria-label="Copy results"
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={handleCopyResults}
                  title="Copy results to clipboard"
                />
              </HStack>
            </Flex>
            
            <Box borderWidth={1} borderRadius="md">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Account Name</Th>
                    <Th>Primary Contact</Th>
                    <Th>Email</Th>
                    <Th>Vendor</Th>
                    <Th isNumeric>Transactions</Th>
                    <Th isNumeric>Total Amount</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {results.clients.map((client, index) => (
                    <Tr key={`${client.customer_id}-${client.vendor_id}`}>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">
                            {client.customer_name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ID: {client.customer_id}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {client.customer_primary_contact || '—'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {client.customer_email || '—'}
                        </Text>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text>{client.vendor_name}</Text>
                          <Text fontSize="xs" color="gray.500">
                            ID: {client.vendor_id}
                          </Text>
                        </VStack>
                      </Td>
                      <Td isNumeric>
                        <Badge colorScheme="blue">
                          {client.journal_entries_count}
                        </Badge>
                      </Td>
                      <Td isNumeric>
                        <Text
                          color={client.total_amount >= 0 ? "green.600" : "red.600"}
                          fontWeight="semibold"
                        >
                          {formatCurrency(client.total_amount)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}

        {results && results.summary.total_clients_found === 0 && (
          <Alert status="info">
            <AlertIcon />
            <AlertTitle>No Results Found</AlertTitle>
            <AlertDescription>
              No accounts found with the selected vendor in their general ledger.
            </AlertDescription>
          </Alert>
        )}
      </VStack>
    </Box>
  );
}; 
